import { HttpClient, HttpEvent, HttpParams } from "@angular/common/http";
import { EventEmitter, Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { environment } from "src/environments/environment";
import { Cargo } from "../interfaces/cargo";
import { CargoPaymentDetail } from "../interfaces/cargoPayment";
import { ParamsCargoList } from "../interfaces/paramsCargoList";
import { Endpoints } from "../resources/endpoints";
import { Utils } from "../resources/utils";
import { Fmt } from "../messages/fmt";
import { FirebaseApi } from "../classes/firebase-api";
import { DateManager } from "../managers/date.manager";
import { FulfillmentCargo } from "../interfaces/fulfillmentDocument";
import { DateEnum } from "../enums/date.enum";
import { Notification } from "../interfaces/notification";
import { DispatchStatePhase } from "../interfaces/dispatchStatePhase";
import { ResponseList } from "../interfaces/response-list";

@Injectable({ providedIn: 'root' })
export class CargoService {
  unsubscribeCargoChanges: { cargoId: string, id: string, unsubscribe: () => void, eventEmitter: EventEmitter<Cargo> }[] = [];
  public static secondsLeftToUploadEvidences = null;
  public static timeLeftToUploadEvidences = null;
  public static uncares: Array<Cargo> = [];
  public static filters: string = '';
  public static total: number = 0;
  public static onUncareStacked: EventEmitter<any> = new EventEmitter();
  public static clickMadeToStartSounds: boolean = false;
  private gpsTrackingAlertSubject = new BehaviorSubject<Notification | null>(null);
  public gpsTrackingAlert$ = this.gpsTrackingAlertSubject.asObservable();
  private cargoFieldsDDMMYYYY: string[] = ['containerExpirationDate', 'receiptDateDocuments', 'startTripDate', 'expeditionDateBill', 'expeditionDateJournal'];
  private cargoFieldsUTC0: string[] = [
    'dateLoad', 'downloadDate', 'endDate', 'startDate', 'creationDate', 'date', 'approvalDate', 'creationDate',
    'dateTimeDownload', 'dateAcceptedFastPayment', 'arrival', 'entry', 'departure', 'monitorHubEndDate', 'monitorHubStartDate'];
  constructor(
    private http: HttpClient,
    private endpoints: Endpoints,
    public utils: Utils,
  ) { }

  public static cron() {
    if (this.secondsLeftToUploadEvidences !== null && this.secondsLeftToUploadEvidences > 0) {
      this.timeLeftToUploadEvidences = DateManager.secondsToCronometer(this.secondsLeftToUploadEvidences);
      this.secondsLeftToUploadEvidences--;
      setTimeout(() => this.cron(), 1000);
    } else {
      this.secondsLeftToUploadEvidences = null;
      this.timeLeftToUploadEvidences = null;
    }
  }

  public cargoList(paramsCargoList: ParamsCargoList, customeFilters?: string): Observable<ResponseList<Cargo>> {
    let params = new HttpParams();

    if (!this.utils.isEmpty(paramsCargoList.pageKey))
      params = params.append('pageKey', paramsCargoList.pageKey.toString());
    if (!this.utils.isEmpty(paramsCargoList.pageSize))
      params = params.append('pageSize', paramsCargoList.pageSize.toString());
    if (!this.utils.isEmpty(paramsCargoList.consecutive))
      params = params.append('consecutive', paramsCargoList.consecutive.toString());
    if (!this.utils.isEmpty(paramsCargoList.state))
      params = params.append('state', paramsCargoList.state);
    if (!this.utils.isEmpty(paramsCargoList.states))
      params = params.append('states', JSON.stringify(paramsCargoList.states));
    if (!this.utils.isEmpty(paramsCargoList.itineraryId))
      params = params.append('itineraryId', paramsCargoList.itineraryId);
    if (!this.utils.isEmpty(paramsCargoList.stateDispatch))
      params = params.append('stateDispatch', paramsCargoList.stateDispatch);
    if (!this.utils.isEmpty(paramsCargoList.anomalies))
      params = params.append('anomalies', JSON.stringify(paramsCargoList.anomalies));
    if (!this.utils.isEmpty(paramsCargoList.news))
      params = params.append('news', paramsCargoList.news);
    if (!this.utils.isEmpty(paramsCargoList.nit))
      params = params.append('nit', paramsCargoList.nit);
    if (!this.utils.isEmpty(paramsCargoList.exclusive))
      params = params.append('exclusive', paramsCargoList.exclusive.toString());
    if (!this.utils.isEmpty(paramsCargoList.origin))
      params = params.append('origin', paramsCargoList.origin);
    if (!this.utils.isEmpty(paramsCargoList.dateLoad))
      params = params.append('dateLoad', paramsCargoList.dateLoad);
    if (!this.utils.isEmpty(paramsCargoList.endDate))
      params = params.append('endDate', paramsCargoList.endDate);
    if (!this.utils.isEmpty(paramsCargoList.creationDateInitial))
      params = params.append('creationDateInitial', paramsCargoList.creationDateInitial);
    if (!this.utils.isEmpty(paramsCargoList.creationDateFinal))
      params = params.append('creationDateFinal', paramsCargoList.creationDateFinal);
    if (!this.utils.isEmpty(paramsCargoList.journal))
      params = params.append('journal', paramsCargoList.journal.toString());
    if (!this.utils.isEmpty(paramsCargoList.assignedDriver))
      params = params.append('assignedDriver', paramsCargoList.assignedDriver.toString());
    if (!this.utils.isEmpty(paramsCargoList.confirmedDriver))
      params = params.append('confirmedDriver', paramsCargoList.confirmedDriver.toString());

    if (customeFilters) {
      const cleanedString = customeFilters.startsWith('&') ? customeFilters.slice(1) : customeFilters;
      const paramsArray = cleanedString.split('&');

      paramsArray.forEach(param => {
        const [key, value] = param.split('=');
        params = params.append(key, value);
      });
    }

    return this.http.get<ResponseList<Cargo>>(
      `${environment.urlServerTeclogi}${this.endpoints.urlCargoList}`,
      { params }
    );
  }

  public moveAdvance(origin: string, destination: string) {
    return this.http.post(
      `${environment.urlServerTeclogi}${this.endpoints.cargoMoveAdvance}?originCargoConsecutive=${origin}&destinationCargoConsecutive=${destination}`,
      null
    );
  }

  public cargoListByFilters(paramsFilter: string): Observable<ResponseList<Cargo>> {
    let url: string = environment.urlServerTeclogi + this.endpoints.urlCargoList + paramsFilter;
    return this.http.get<ResponseList<Cargo>>(url);
  }

  /**
   * Returns the minimal payload required by the global tracking map.
   * Full cargo details must be requested only when a user selects a cargo.
   */
  public cargoMapPositions(paramsFilter: string): Observable<ResponseList<Cargo>> {
    const filters = paramsFilter && paramsFilter.charAt(0) === '&'
      ? paramsFilter.substring(1)
      : paramsFilter || '';
    const separator = filters ? '?' : '';
    const url = `${environment.urlServerTeclogi}${this.endpoints.cargoMapPositions}${separator}${filters}`;

    return this.http.get<ResponseList<Cargo>>(url);
  }

  public getDispatchStates(): Observable<DispatchStatePhase[]> {
    return this.http.get<DispatchStatePhase[]>(
      `${environment.urlServerTeclogi}${this.endpoints.cargoDispatchStates}`
    );
  }

  public downloadCargoTraceability(cargoId: string) {
    return this.http.get(
      `${environment.urlServerTeclogi}${this.endpoints.downloadCargoTraceability}${cargoId}`,
      { responseType: 'blob' }
    );
  }

  public updateFields(cargo: Cargo) {
    return this.http.put(`${environment.urlServerTeclogi}${this.endpoints.updateCargo}`, cargo);
  }

  /** Override manual del perfil de riesgo del servicio (solo equipo de Seguridad). */
  public overrideRiskProfile(cargoId: string, riskProfile: { id: string, name?: string }, overrideRiskValidation?: boolean) {
    let params = new HttpParams();
    if (overrideRiskValidation) params = params.set('overrideRiskValidation', 'true');
    return this.http.put(
      `${environment.urlServerTeclogi}${this.endpoints.overrideRiskProfile}${cargoId}`,
      riskProfile,
      { params }
    );
  }

  /**
   * Pre-chequeo del minimo de viajes que exige el perfil de riesgo del servicio, sin ejecutar la
   * asignacion real. Permite consultarlo antes de pedirle al usuario que complete la inspeccion del
   * vehiculo. Reutiliza en el backend la misma regla que valida la asignacion real.
   */
  public validateVehicleTripRequirement(cargoId: string, licensePlate: string, assignVehicleFullAccess: boolean): Observable<any> {
    let params = new HttpParams().set('cargoId', cargoId).set('licensePlate', licensePlate);
    if (assignVehicleFullAccess) params = params.set('assignVehicleFullAccess', 'true');
    return this.http.get(
      `${environment.urlServerTeclogi}${this.endpoints.validateVehicleTripRequirement}`,
      { params }
    );
  }

  private sanitizeCargo(cargo: Cargo): Cargo {
    if (!cargo) return null;
    Object.keys(cargo).forEach(key => {
      if (cargo[key] === null || cargo[key] === undefined)
        delete cargo[key];
      else if (typeof cargo[key] === 'object') {
        if (Array.isArray(cargo[key])) {
          cargo[key] = cargo[key].map(item => {
            if (item && this.utils.isDefined(item.seconds) && this.utils.isDefined(item.nanoseconds))
              return this.transformCargoDate(item, key);
            else if (this.utils.isDefined(item) && typeof item === 'object')
              return this.sanitizeCargo(item);
            return item;
          });
        }
        else if (this.utils.isDefined(cargo[key].seconds) && this.utils.isDefined(cargo[key].nanoseconds))
          cargo[key] = this.transformCargoDate(cargo[key], key);
        else {
          cargo[key] = this.sanitizeCargo(cargo[key]);
          if (!Array.isArray(cargo[key]) && Object.keys(cargo[key]).length === 0)
            delete cargo[key];
        }
      }
    });
    return cargo as Cargo;
  }

  private transformCargoDate(item: { seconds: number, nanoseconds: number }, key: string): string {
    const date = new Date(item.seconds * 1000 + item.nanoseconds / 1000000);
    if (this.cargoFieldsUTC0.includes(key))
      return DateManager.utcTransform(date);
    else if (this.cargoFieldsDDMMYYYY.includes(key))
      return DateManager.dateToString(date, 'DD/MM/YYYY');
    else
      return DateManager.dateToString(date);
  }

  private checkExistentSubscription(cargoId: string): { id: string, eventEmitter: EventEmitter<Cargo> } {
    const subscription = this.unsubscribeCargoChanges.find(sub => sub.cargoId === cargoId);
    if (!subscription) return null;
    const newSubscription = { ...subscription, id: new Date().getTime().toString() };
    this.unsubscribeCargoChanges.push(newSubscription);
    return { id: newSubscription.id, eventEmitter: newSubscription.eventEmitter };
  }

  public watch(cargoId: string): { id: string, eventEmitter: EventEmitter<Cargo> } {
    const existentSubscription = this.checkExistentSubscription(cargoId);
    if (existentSubscription) return existentSubscription;

    const eventEmitter = new EventEmitter<Cargo>();
    const activeSubscription = {
      cargoId,
      id: new Date().getTime().toString(),
      unsubscribe: () => { },
      eventEmitter
    };
    const openSubscription = FirebaseApi.firestore.collection('Cargo').doc(cargoId).onSnapshot((snapshot) => {
      const data = snapshot.data();
      if (data) {
        const sanitizedCargo = this.sanitizeCargo(data);
        if (sanitizedCargo) eventEmitter.emit(sanitizedCargo);
      }
    });

    activeSubscription.unsubscribe = openSubscription;
    this.unsubscribeCargoChanges.push(activeSubscription);
    return { id: activeSubscription.id, eventEmitter };
  }

  public composeWatchByConsecutive(consecutive: number): Promise<{ active: boolean, id?: string, eventEmitter?: EventEmitter<Cargo> }> {
    return FirebaseApi.firestore
      .collection('Cargo')
      .where('consecutive', '==', consecutive)
      .get()
      .then(querySnapshot => {
        if (querySnapshot.empty) {
          console.error('No se encontró ningún documento con el valor especificado.');
          return { active: false };
        }
        else {
          const { id, eventEmitter } = this.watch(querySnapshot.docs[0].id);
          return { active: true, id, eventEmitter };
        }
      }
      ).catch(error => {
        console.error('Error al buscar el documento:', error);
        return { active: false };
      });
  }

  public stopWatch(id?: string) {
    if (id) {
      const subscription = this.unsubscribeCargoChanges.findIndex(sub => sub.id === id);
      if (subscription !== -1) {
        const sub = this.unsubscribeCargoChanges[subscription];
        const cargoIdHasOtherSubscription = this.unsubscribeCargoChanges.some(s => s.cargoId === sub.cargoId && s.id !== sub.id);
        if (!cargoIdHasOtherSubscription) {
          sub.unsubscribe();
          sub.eventEmitter.complete();
        }
        this.unsubscribeCargoChanges.splice(subscription, 1);
      }
    } else {
      this.unsubscribeCargoChanges.forEach(sub => {
        sub.unsubscribe();
        sub.eventEmitter.complete();
      });
      this.unsubscribeCargoChanges = [];
    };
  }

  public stopWatchByCargoId(cargoId: string) {
    const subscription = this.unsubscribeCargoChanges.findIndex(sub => sub.cargoId === cargoId);
    if (subscription !== -1) {
      this.unsubscribeCargoChanges[subscription].unsubscribe();
      this.unsubscribeCargoChanges[subscription].eventEmitter.complete();
      this.unsubscribeCargoChanges.splice(subscription, 1);
    }
  }

  public updateSecureCargo(originalCargo: Cargo, newCargo: Cargo): Cargo {
    // Duplicar la carga original
    const duplicatedCargo = JSON.parse(JSON.stringify(originalCargo));

    // Función auxiliar para verificar si un valor está definido
    const isDefined = (value: any): boolean => {
      if (value === null || value === undefined) return false;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'object') return Object.keys(value).length > 0;
      return true;
    };

    const protectedKeys = new Set(['payments']);

    const updateFieldsRecursively = (target: any, source: any): void => {
      if (!source || typeof source !== 'object') return;

      Object.keys(source).forEach(key => {
        if (protectedKeys.has(key)) return;
        const sourceValue = source[key];
        const targetValue = target[key];
        if (isDefined(sourceValue)) {
          if (typeof sourceValue === 'object' && !Array.isArray(sourceValue) &&
            targetValue && typeof targetValue === 'object' && !Array.isArray(targetValue)) {
            updateFieldsRecursively(targetValue, sourceValue);
          } else {
            target[key] = sourceValue;
          }
        }
      });
    };
    updateFieldsRecursively(duplicatedCargo, newCargo);
    return duplicatedCargo;
  }

  public registerVehicleInMonitor(cargoId: string, start: string, end: string) {
    const fStartDate = DateManager.formatDate(start);
    const fEndDate = DateManager.formatDate(end);

    return this.http.post(`${environment.urlServerTeclogi}${this.endpoints.registerVehicleInMonitor}`, {
      cargoId: cargoId,
      startDate: fStartDate,
      endDate: fEndDate
    });
  }

  public initialFulfillConsignment(consignments: { cargoId: string, consignment: { id: string, dateTimeDownload: string, destinationId: number, addressId: number }[] }) {
    return this.http.post(`${environment.urlServerTeclogi}${this.endpoints.initialFulfillConsignments}`, consignments);
  }

  static get cargos(): Cargo[] {
    return CargoService.uncares;
  }

  public static stack(cargos: Cargo[], filters: string = '', total: number = 0) {
    CargoService.uncares = cargos;
    CargoService.filters = filters;
    CargoService.total = total;
    if (cargos && cargos.length) CargoService.onUncareStacked.emit(cargos);
  }

  public approvedBelowSiceTac(cargoId: string) {
    return this.http.put(
      environment.urlServerTeclogi + this.endpoints.approvedBelowSiceTac + cargoId + "&approved=true", null
    )
  }

  public getCargoPayments(cargoId: string, state?: string): Observable<CargoPaymentDetail[]> {
    let params = new HttpParams();
    if (!this.utils.isEmpty(state)) {
      params = params.append('state', state);
    }
    return this.http.get<CargoPaymentDetail[]>(
      `${environment.urlServerTeclogi}${Fmt.string(this.endpoints.urlCargoPayments, cargoId)}`,
      { params }
    );
  }

  public getCargoFulFillment(cargoId: string, addressId: number, destinationId: number): Observable<FulfillmentCargo[]> {
    let params = new HttpParams();
    params = params.append('cargoId', cargoId);
    params = params.append('addressId', addressId.toString());
    params = params.append('destinationId', destinationId.toString());
    return this.http.get<FulfillmentCargo[]>(
      `${environment.urlServerTeclogi}${this.endpoints.cargoCompliments}`,
      { params }
    );
  }

  public updateCargoFulFillment(body: FulfillmentCargo): Observable<FulfillmentCargo> {
    return this.http.put<FulfillmentCargo>(
      `${environment.urlServerTeclogi}${this.endpoints.cargoCompliments}`,
      body
    );
  }

  public createCargoFulFillment(body: FulfillmentCargo): Observable<FulfillmentCargo> {
    return this.http.post<FulfillmentCargo>(
      `${environment.urlServerTeclogi}${this.endpoints.cargoCompliments}`,
      body
    );
  }

  public duplicateCargo(newCargos: Cargo[], cargoId: string): Observable<Cargo[]> {
    return this.http.post<Cargo[]>(
      `${environment.urlServerTeclogi}${this.endpoints.duplicateCargo}/${cargoId}`,
      newCargos
    );
  }

  public emitGpsTrackingAlert(notification: Notification | null): void {
    this.gpsTrackingAlertSubject.next(notification);
  }

  public createConsolidatedCargos(cargos: Cargo[]): Observable<Cargo[]> {
    return this.http.post<Cargo[]>(
      `${environment.urlServerTeclogi}${this.endpoints.createConsolidatedCargo}`,
      cargos
    );
  }

}
